import { z } from 'zod';

// Zod schema for QR data validation
export const QRDataSchema = z.object({
  id: z.number().positive(),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email format'),
  dni: z.string().min(1, 'DNI is required'),
  type: z.enum(['userQR']).default('userQR'),
});

// TypeScript type derived from the schema
export type UserQRData = z.infer<typeof QRDataSchema>;

export type UserQRInputData = Pick<
  UserQRData,
  'id' | 'first_name' | 'last_name' | 'email' | 'dni'
>;

/**
 * Transform User data to QR data object
 */
export const getUserQRData = (user: UserQRInputData): UserQRData => {
  const qrData: UserQRData = {
    id: user.id,
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    dni: user.dni,
    type: 'userQR',
  };

  // Validate the data before returning
  return QRDataSchema.parse(qrData);
};

/**
 * Transform QR data to JSON string for QR code generation
 */
export const qrDataToString = (data: UserQRData): string => {
  // Validate the data before stringifying
  const validatedData = QRDataSchema.parse(data);
  return JSON.stringify(validatedData);
};

/**
 * Parse QR code string back to QR data object
 * @param qrString - The JSON string from a QR code
 * @returns Parsed and validated QR data
 * @throws ZodError if the data is invalid
 */
export const parseQRString = (qrString: string): UserQRData => {
  try {
    const parsedData = JSON.parse(qrString);
    return QRDataSchema.parse(parsedData);
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error('Invalid JSON format in QR code');
    }
    throw error; // Re-throw Zod validation errors
  }
};

/**
 * Safe QR string parsing that returns null for invalid data
 * @param qrString - The JSON string from a QR code
 * @returns Parsed QR data or null if invalid
 */
export const safeParseQRString = (qrString: string): UserQRData | null => {
  try {
    return parseQRString(qrString);
  } catch {
    return null;
  }
};

/**
 * Check if a QR string contains valid QR data
 * @param qrString - The JSON string from a QR code
 * @returns true if valid, false otherwise
 */
export const isValidQRString = (qrString: string): boolean => {
  return safeParseQRString(qrString) !== null;
};
